import * as React from "react";
import {
  Box,
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DoneIcon from "@mui/icons-material/Done";

import { GlassOrderStatus } from "../../../enum/GlassOrder";
import { GlassOrder } from "../../../interfaces/GlassOrder";
import { useUpdateGlassOrderStatus } from "../../../hooks/query/useGlassOrders";
import CalendarDropdown from "../../../components/CalendarDropdown/CalendarDropdown";
import { tl } from "../../../components/translate";

export interface HandleGlassOrderStatusProps {
  glassOrder: GlassOrder;
  setGlassOrder: React.Dispatch<React.SetStateAction<GlassOrder | null>>;
  setGlassOrderData?: React.Dispatch<
    React.SetStateAction<{ results: GlassOrder[]; total: number } | null>
  >;
  onStatusUpdated?: (updatedOrder: GlassOrder) => void;
}

export type GlassOrderStatusActionType =
  | "markInProgress"
  | "markCompleted"
  | "markDelivered"
  | "markCancelled"
  | "markPending";

const actionStatusMap: Record<GlassOrderStatusActionType, GlassOrderStatus> = {
  markInProgress: GlassOrderStatus.IN_PROGRESS,
  markCompleted: GlassOrderStatus.COMPLETED,
  markDelivered: GlassOrderStatus.DELIVERED,
  markCancelled: GlassOrderStatus.CANCELLED,
  markPending: GlassOrderStatus.PENDING
};

const actionLabelMap: Record<GlassOrderStatusActionType, string> = {
  markInProgress: "Mark In Progress",
  markCompleted: "Mark Completed",
  markDelivered: "Mark Delivered",
  markCancelled: "Mark Cancelled",
  markPending: "Mark Pending"
};

const optionsForStatus: Record<GlassOrderStatus, GlassOrderStatusActionType[]> = {
  [GlassOrderStatus.PENDING]: ["markInProgress", "markCompleted", "markDelivered", "markCancelled"],
  [GlassOrderStatus.IN_PROGRESS]: ["markCompleted", "markDelivered", "markCancelled"],
  [GlassOrderStatus.COMPLETED]: ["markDelivered", "markCancelled"],
  [GlassOrderStatus.DELIVERED]: ["markCancelled"],
  [GlassOrderStatus.CANCELLED]: ["markPending", "markInProgress"]
};

export default function HandleGlassOrderStatus({
  glassOrder,
  setGlassOrder,
  setGlassOrderData,
  onStatusUpdated
}: HandleGlassOrderStatusProps): React.JSX.Element | null {
  const updateStatusMutation = useUpdateGlassOrderStatus();
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [openMenu, setOpenMenu] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [actionAfter, setActionAfter] = React.useState<GlassOrderStatusActionType | null>(null);
  const [deliveryDate, setDeliveryDate] = React.useState(new Date().toISOString());
  const [openDeliveryDateSelect, setOpenDeliveryDateSelect] = React.useState(false);
  const [statusOptions, setStatusOptions] = React.useState<GlassOrderStatusActionType[]>([]);

  React.useEffect(() => {
    const currentStatus = (glassOrder?.status as GlassOrderStatus) || GlassOrderStatus.PENDING;
    const opts = optionsForStatus[currentStatus] || [];
    setStatusOptions(opts);
    setSelectedIndex(0);
  }, [glassOrder?.status]);

  if (!statusOptions.length) {
    return null;
  }

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpenMenu(false);
  };

  const applyStatusUpdate = (resData: GlassOrder) => {
    setGlassOrder(resData);
    if (setGlassOrderData) {
      setGlassOrderData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          results: prev.results.map((item) => (item.id === resData.id ? resData : item))
        };
      });
    }
    if (onStatusUpdated) {
      onStatusUpdated(resData);
    }
  };

  const handleClick = async (option: GlassOrderStatusActionType, index: number) => {
    setOpenMenu(false);
    setSelectedIndex(index);

    if (option === "markDelivered") {
      setActionAfter(option);
      setOpenDeliveryDateSelect(true);
    } else {
      const targetStatus = actionStatusMap[option];
      try {
        const res = await updateStatusMutation.mutateAsync({
          id: glassOrder.id,
          payload: { status: targetStatus }
        });
        if (res?.data) {
          applyStatusUpdate(res.data);
        }
      } catch {
        // error notification handled by mutation
      }
    }
  };

  const handleMarkDelivered = async () => {
    setOpenDeliveryDateSelect(false);
    try {
      const res = await updateStatusMutation.mutateAsync({
        id: glassOrder.id,
        payload: {
          status: GlassOrderStatus.DELIVERED,
          deliveryDate
        }
      });
      if (res?.data) {
        applyStatusUpdate(res.data);
      }
    } catch {
      // error notification handled by mutation
    }
  };

  const primaryOption = statusOptions[selectedIndex] || statusOptions[0];

  return (
    <>
      <ButtonGroup
        size="small"
        variant="outlined"
        color="primary"
        ref={anchorRef}
        aria-label="glass order status actions"
      >
        <Button
          data-testmation={primaryOption}
          disabled={updateStatusMutation.isLoading}
          onClick={() => {
            handleClick(primaryOption, selectedIndex);
          }}
        >
          {actionLabelMap[primaryOption]}
        </Button>
        {statusOptions.length > 1 && (
          <Button
            size="small"
            color="primary"
            disabled={updateStatusMutation.isLoading}
            aria-owns={openMenu ? "glass-order-menu-list-grow" : undefined}
            aria-haspopup="true"
            onClick={() => setOpenMenu((prevOpen) => !prevOpen)}
            data-testmation="handleGlassOrderDownArrow"
          >
            <ArrowDropDownIcon />
          </Button>
        )}
      </ButtonGroup>

      <Popper
        style={{ zIndex: 1300 }}
        open={openMenu}
        anchorEl={anchorRef.current}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...TransitionProps}
            style={{ transformOrigin: placement === "bottom" ? "center top" : "center bottom" }}
          >
            <Paper id="glass-order-menu-list-grow">
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList>
                  {statusOptions.map((option, index) => (
                    <MenuItem
                      key={option}
                      selected={index === selectedIndex}
                      onClick={() => {
                        handleClick(option, index);
                      }}
                      data-testmation={option}
                    >
                      {actionLabelMap[option]}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      <Popper
        style={{ zIndex: 1300 }}
        open={openDeliveryDateSelect}
        anchorEl={anchorRef.current}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...TransitionProps}
            style={{ transformOrigin: placement === "bottom" ? "center top" : "center bottom" }}
          >
            <Paper id="glass-order-delivery-date-popover">
              <ClickAwayListener onClickAway={() => setOpenDeliveryDateSelect(false)}>
                <div
                  style={{
                    padding: "16px",
                    border: "1px solid #908f8f",
                    boxShadow: "0px 0px 3px 3px #c7c7c754"
                  }}
                >
                  <CalendarDropdown
                    fullwidth
                    TextFieldProps={{ variant: "outlined", size: "small" }}
                    label="Delivery Date"
                    placeholder="Select delivery date"
                    date={deliveryDate ? new Date(deliveryDate) : new Date()}
                    maxValidDate={new Date()}
                    onChange={(val) => {
                      if (val) {
                        const d = val instanceof Date ? val : new Date(val);
                        if (!Number.isNaN(d.getTime())) {
                          setDeliveryDate(d.toISOString());
                        }
                      }
                    }}
                  />
                  <Box component="div" textAlign="right" marginTop="16px">
                    <Button
                      style={{ marginLeft: "32px" }}
                      onClick={() => setOpenDeliveryDateSelect(false)}
                    >
                      {tl("booking.cancel")}
                    </Button>
                    <Button
                      style={{ marginLeft: "8px" }}
                      variant="contained"
                      color="primary"
                      disabled={updateStatusMutation.isLoading}
                      onClick={() => {
                        handleMarkDelivered();
                      }}
                    >
                      {actionAfter ? actionLabelMap[actionAfter] : "Confirm"}
                      <DoneIcon style={{ marginLeft: "5px" }} />
                    </Button>
                  </Box>
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
